import { FULL_ADDRESS_OTHER_QUESTIONS } from 'constants/questions';
import { IFormDefinition, IFormQuestion, IFormSubmitPayload } from 'types';
import { isEmptyValue, removeEmptyValues } from './object';
import { is3PT } from './policy';
import { getFormById, submitForm } from 'services';

const comparators = {
  is: R.equals,
  not: R.complement(R.equals),
  valid: R.complement(R.isNil),
  invalid: R.isNil,
  lt: (value, schemaVal) => !isEmptyValue(value) && R.lt(value, schemaVal),
  gt: (value, schemaVal) => !isEmptyValue(value) && R.gt(value, schemaVal),
  contains: (value, schemaVal) =>
    Array.isArray(value) && value.includes(schemaVal),
  // Single-value answer is one of a list (radio with several trigger keys).
  in: (value, schemaVal) =>
    Array.isArray(schemaVal) && schemaVal.includes(value),
  // Array answer shares at least one element with a list (checkbox-group
  // with several trigger keys). Used by survey-campaign follow-up textareas.
  containsAny: (value, schemaVal) =>
    Array.isArray(value) &&
    Array.isArray(schemaVal) &&
    value.some((v) => schemaVal.includes(v)),
  vehicleAmount: (formValues) => {
    const subType =
      formValues.policySubTypeName || formValues.policySubTypeId || '';
    return !is3PT(subType);
  },
};

export const getAllQuestions = (formDef) => {
  if (formDef?.pages) {
    return formDef.pages.reduce((acc, page) => {
      return R.concat(page.questions || [], acc);
    }, []);
  }

  return formDef?.questions || [];
};

const getAllQuestionsToDisplay = (formDef, data) => {
  const questions = getAllQuestions(formDef);
  return getQuestionsToDisplay(questions, data);
};

export const castValue = (value: any, toCast: string) => {
  switch (toCast) {
    case 'array':
      return [value];
    case 'boolean':
      return value === 'true';
    case 'number':
      return Number(value);
    default:
      return value;
  }
};

const mapArrayValues = (items, fields) => {
  return items.reduce((iAcc, v) => {
    const fieldsToDisplay = getQuestionsToDisplay(fields, v);
    const reduced = fieldsToDisplay.reduce((acc, f) => {
      const rawValue = v[f.key];
      const fieldVal =
        f.fields && Array.isArray(rawValue)
          ? mapArrayValues(rawValue, f.fields)
          : castValue(rawValue, f.castAs);
      return R.assoc(f.key, fieldVal, acc);
    }, {});

    // Get rid of empty objects
    if (!isEmptyValue(removeEmptyValues(reduced))) {
      iAcc.push(reduced);
    }

    return iAcc;
  }, []);
};

export const shouldDisplay = (display = [], formValues) => {
  // Should not have any schema error
  return !R.any(({ when, comparator, ...schema }) => {
    const customComparator = comparators[comparator];
    if (customComparator) {
      return !customComparator(formValues);
    }

    const value = formValues[when];

    // Find error in schema. A comparator this build doesn't know (e.g. a
    // definition served by a newer API) must not throw and hide the whole
    // form — treat it as satisfied.
    return Object.keys(schema).find((key) => {
      const fn = comparators[key];
      if (!fn) return false;
      return !fn(value, schema[key]);
    });
  }, display);
};

export const shouldDisable = (disabled: boolean | any[], formValues) => {
  if (Array.isArray(disabled)) {
    return shouldDisplay(disabled, formValues);
  }
  return disabled;
};

export const getQuestionsToDisplay = (
  questions: IFormQuestion[],
  formValues: any,
  keysToIgnore: string[] = [],
  override?: any,
) => {
  return questions.reduce((acc, q) => {
    if (shouldDisplay(q.display, formValues)) {
      const question = { ...q, ...override };
      if (question.key === 'address' && formValues.showFullAddress) {
        const addressQuestions = question.column
          ? FULL_ADDRESS_OTHER_QUESTIONS.map((q) => ({
              ...q,
              column: question.column,
              basic: question.basic,
              className: 'halfWidth nested',
            }))
          : FULL_ADDRESS_OTHER_QUESTIONS;
        if (R.includes(question.key, keysToIgnore)) {
          return R.concat(acc, addressQuestions);
        }
        return R.concat(acc, [question, ...addressQuestions]);
      } else {
        acc.push(question);
      }
    }
    return acc;
  }, []);
};

export const getSubmitPayload = (
  formDefinition: IFormDefinition,
  data: any,
  metadata?: any,
): IFormSubmitPayload => {
  const questionsToDisplay = getAllQuestionsToDisplay(formDefinition, data);
  const questions = questionsToDisplay.reduce((acc, question) => {
    const { key, castAs, fields } = question;
    const rawValue = data[key];

    // Ignore undefined and null values
    if (R.isNil(rawValue)) {
      return acc;
    }

    const value = fields
      ? mapArrayValues(rawValue, fields)
      : castValue(rawValue, castAs);

    if (!isEmptyValue(value)) {
      acc.push({
        key,
        value,
      });
    }

    return acc;
  }, []);

  return {
    id: formDefinition.id,
    sk: formDefinition.sk,
    questions,
    metadata,
  };
};

export const submitFormById = async (formId: string, data, metadata) => {
  const formDef = await getFormById(formId);
  if (formDef.data) {
    const payload = getSubmitPayload(formDef.data, data, metadata);
    const response = await submitForm(payload);
    return { payload, response };
  }
};
